import { compose, filter, has, keyBy, map, uniqBy, values } from 'lodash/fp';
import type MediumUser from '../../../../types/MediumUser';
import type MiniUser from '../../../../types/MiniUser';
import type Role from '../../../../types/Role';
import type { CurrentUserDto, UserListDto, UserProfileDto } from '../../../../types/UserDtos';
import api from '../../../api';
import * as ROUTES from '../../../routes';
import { interpolateAppUrl } from '../Url';

type CurrentOrUserProfileDto = CurrentUserDto | UserProfileDto;
type CurrentOrUserListDto = CurrentUserDto | UserListDto;
type CurrentOrMediumUser = CurrentUserDto | MediumUser;
type MiniUserWithSecondaryRevieweeMarker = MiniUser & { isSecondaryReviewee?: true };

export function isIc(user: CurrentOrUserListDto) {
    return !hasBasicHr(user) && !isManager(user);
}

export function isAnyAdmin(user: CurrentOrUserListDto) {
    return isAdmin(user) || hasBasicHr(user);
}

export function isDecisionMaker(user: CurrentOrUserListDto, includeReportsOfCeo = true) {
    return (
        isAnyAdmin(user) ||
        !hasManager(user) ||
        (includeReportsOfCeo && user.manager !== null && !user.manager.managerId)
    );
}

export function getLogoutRedirectUrl(user: CurrentOrUserListDto): string {
    if (window.location.hostname === 'localhost') {
        return getLoginUrl();
    }

    if (isDecisionMaker(user)) {
        return 'https://www.small-improvements.com/logged-out';
    }
    if (isManager(user)) {
        return 'https://www.small-improvements.com/logged-out-mg';
    }
    return 'https://www.small-improvements.com/logged-out-ic';
}

export function getLoginUrl(redirect?: string) {
    return `/login${redirect ? `?continue=${encodeURIComponent(redirect)}` : ''}`;
}

export function hasManager(user: MiniUser) {
    return !!user.managerId;
}

export function getRoleInfo(user: CurrentOrUserListDto) {
    return {
        ADMIN: {
            isActive: isAdmin(user),
            key: 'ADMIN',
            name: 'Tech Admin',
        },
        HR: {
            isActive: isHr(user),
            key: 'HR',
            name: 'HR Admin',
        },
        ASSISTANT: {
            isActive: isAssistant(user),
            key: 'ASSISTANT',
            name: 'HR Assistant',
        },
    };
}

export function enumerateRoles(user: CurrentOrUserListDto) {
    return compose(
        filter(r => r.isActive),
        values,
        getRoleInfo
    )(user);
}

export function isAdmin(user: CurrentOrUserListDto) {
    return user.isAdmin;
}

export function hasAnyRole(user: CurrentOrUserListDto) {
    return hasBasicHr(user) || isGuest(user) || !isActive(user);
}

export function hasBasicHr(user: CurrentOrUserListDto) {
    return isHr(user) || isAssistant(user);
}

export function isSuperUser(user: CurrentOrUserListDto) {
    return isHr(user) && isAdmin(user);
}

export function isHr(user: CurrentOrUserListDto) {
    return user.isHR;
}

export function isAssistant(user: CurrentOrUserListDto) {
    return user.isAssistant;
}

export function isGuest(user: CurrentOrUserListDto) {
    return user.isGuest;
}

export function isActive(user: MiniUser) {
    return user.isActive;
}

export function isFake(user: CurrentOrMediumUser) {
    return user.loginname.endsWith('.fake');
}

export function isAnonymous(user: MiniUser) {
    const ANONYMOUS_ID = 'anonymous--id';
    return user.id === ANONYMOUS_ID;
}

export function isDeleted(user: MiniUser) {
    const DELETED_USER_ID = 'deleted-user-id';
    return user.id === DELETED_USER_ID;
}

export function isTaskUser(user: MiniUser) {
    return user.id === 'automation-bot-id' || (!!user.id && user.id.startsWith('TASK_'));
}

export function isManager(user: CurrentOrUserListDto) {
    return getDirectReports(user).length > 0;
}

export function isWithoutManager(user: CurrentOrMediumUser) {
    return !user.managerId;
}

export function getDirectReports(user: CurrentOrUserListDto) {
    return user.reports || [];
}

export function getUrlToProfile(user: Pick<MiniUser, 'id'>): string {
    return interpolateAppUrl(ROUTES.USER_PROFILE, { id: user.id });
}

export async function getTeamMembersWithProfileUrl(user: CurrentOrUserProfileDto) {
    const teamMembers = await getTeamMembers(user);
    return teamMembers.map(teamMember => ({ ...teamMember, profileUrl: getUrlToProfile(teamMember) }));
}

export async function getTeamMembers(user: CurrentOrUserProfileDto): Promise<MiniUserWithSecondaryRevieweeMarker[]> {
    let teamMates: MiniUser[] = [];
    if (isManager(user)) {
        teamMates = getReports(user);
    } else if (user.managerId) {
        const manager = await api.users.getById(user.managerId);
        let reports = [];
        reports = getReports(manager).filter(u => u.id !== user.id);
        teamMates = [...reports, manager];
    } else if (user.manager) {
        // basic manager info
        teamMates = [user.manager];
    }
    const secondaryReports = !isManager(user) && hasSecondaryReports(user) ? getSecondaryReports(user) : [];
    const teamMembers = [...teamMates, ...secondaryReports];
    return uniqBy('id', teamMembers);
}

export function isTeamMember(members: MiniUser[]) {
    const membersIndex = keyBy(u => u.id, members);
    return (user: MiniUser) => has(user.id, membersIndex);
}

export function getManager(user: CurrentOrUserListDto) {
    if (user.manager) {
        return [user.manager];
    } else {
        return [];
    }
}

function getReports(user: CurrentOrUserProfileDto): MiniUserWithSecondaryRevieweeMarker[] {
    return [...getDirectReports(user), ...getSecondaryReports(user)];
}

export function getSecondaryReports(user: CurrentOrUserProfileDto): MiniUserWithSecondaryRevieweeMarker[] {
    return map(markAsSecondary, user.secondaryReviewees);
}

function markAsSecondary(user: MiniUser): MiniUserWithSecondaryRevieweeMarker {
    return {
        ...user,
        isSecondaryReviewee: true,
    };
}

export function hasSecondaryReports(user: CurrentOrUserProfileDto) {
    return getSecondaryReports(user).length > 0;
}

export async function getManagersReports(user: MiniUser) {
    if (hasManager(user)) {
        const manager = await api.users.getById(user.managerId!);
        return getDirectReports(manager);
    } else {
        return [];
    }
}

export function isUserEqual(user1: MiniUser, user2: MiniUser) {
    return user1.id === user2.id;
}

export type CycleAdminFeature = 'REVIEWS' | '360_FEEDBACK' | 'OBJECTIVES';

export function isAnyCycleAdmin(user: CurrentUserDto) {
    const features: CycleAdminFeature[] = ['REVIEWS', '360_FEEDBACK', 'OBJECTIVES'];
    return features.some(feature => isCycleAdminFor(user, feature));
}

export function isCycleAdminFor(user: CurrentUserDto, feature: CycleAdminFeature): boolean {
    const { cycleAdminRights } = user;
    switch (feature) {
        case 'REVIEWS':
            return !!cycleAdminRights.reviewCycles.length;
        case '360_FEEDBACK':
            return !!cycleAdminRights.feedbackCycles.length;
        case 'OBJECTIVES':
            return !!cycleAdminRights.objectiveCycles.length;
    }
}

export function hasAnySiRole(roles: Role[] | undefined, requiredRoles: Role[]) {
    if (!roles) {
        return false;
    }
    return requiredRoles.some(requiredRole => roles.includes(requiredRole));
}
